import { createReconciliationViewConfig } from "@common/helpers/close-template-task-view-config";
import { FinancialAccountType } from "@common/types/financialAccount";
import { isPlainObject } from "@common/helpers/typeGuards";
import { ViewSchema } from "@common/types/views/views";
import { isFinancialAccountType } from "./closeTemplateTaskViewConfig";

/**
 * Reads the list of financial account types stored on a reconciliation
 * task template's viewConfig. Returns an empty array if none are stored.
 */
export function readReconciliationFinancialAccountTypes(
    raw: Record<string, unknown> | null,
): FinancialAccountType[] {
    if (!raw) {
        return [];
    }
    // Try parsing as a View to extract filters.financialAccountTypes via Zod.
    const parsed = ViewSchema.safeParse(raw);
    const rawTypes = parsed.success
        ? parsed.data.filters?.financialAccountTypes
        : raw.financialAccountTypes;
    if (!Array.isArray(rawTypes)) {
        return [];
    }
    return rawTypes.filter(
        (item): item is FinancialAccountType =>
            typeof item === "string" && isFinancialAccountType(item),
    );
}

/**
 * Reads the specific financial account IDs pinned on a client-specific
 * reconciliation template task. Mutually exclusive with
 * `financialAccountTypes` — when present (non-empty), the task targets only
 * these exact accounts instead of "any account of these types", and only for
 * the entity each account actually belongs to (validated server-side on
 * apply/preview — see `resolveReconciliationViewConfig`).
 *
 * Stored under `filters.accountIds`, the same generic field other view
 * types use to pin exact accounts, so it survives `ViewSchema`/
 * `PersistableViewSchema` validation on the backend without needing a
 * dedicated schema field.
 */
/**
 * Reads `accountIds` from a raw (unvalidated) `filters` blob, used only when
 * the viewConfig fails `ViewSchema` validation. Narrows without casting.
 */
function readRawFilterAccountIds(filters: unknown): unknown {
    return isPlainObject(filters) ? filters.accountIds : undefined;
}

export function readReconciliationFinancialAccountIds(
    raw: Record<string, unknown> | null,
): number[] {
    if (!raw) {
        return [];
    }
    const parsed = ViewSchema.safeParse(raw);
    const rawIds = parsed.success
        ? parsed.data.filters?.accountIds
        : readRawFilterAccountIds(raw.filters);
    if (!Array.isArray(rawIds)) {
        return [];
    }
    return rawIds.filter((item): item is number => typeof item === "number");
}

/**
 * Serialises the financial account targeting for a reconciliation
 * close-template task into the viewConfig shape stored on the template.
 * `financialAccountIds` (pinned exact accounts) and `financialAccountTypes`
 * (any account of these types) are mutually exclusive — passing a non-empty
 * `financialAccountIds` clears `financialAccountTypes`, and vice versa.
 * Uses ViewType.RECONCILIATION so `buildViewConfig` on the backend can
 * recognise and pass the config through.
 */
export function serializeReconciliationCloseTemplateViewConfig(
    financialAccountTypes: FinancialAccountType[],
    financialAccountIds: number[] = [],
) {
    return createReconciliationViewConfig({
        financialAccountTypes,
        financialAccountIds,
    });
}
